import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCreditCard,
  faWallet,
  faExclamationTriangle,
  faPhone,
  faEnvelope,
  faGlobe,
  faCheckCircle,
  faStar,
  faHeart,
  faRocket,
  faEye,
  faLock,
  faBan,
  faClock,
  faTimesCircle,
  faShieldAlt
} from '@fortawesome/free-solid-svg-icons';

const RefundPolicy = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const staggerItem = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 },
    transition: { duration: 0.5 }
  };

  const refundSections = [
    {
      id: 1,
      title: "No Cash Refunds",
      icon: faTimesCircle,
      color: "from-red-500 to-red-600",
      bgColor: "from-red-50 to-red-100",
      borderColor: "border-red-200",
      items: [
        "We do not offer cash refunds or bank transfers for any payments made on our platform.",
        "Instead, any eligible refund amount will be credited to the user's IINSAF Wallet."
      ]
    },
    {
      id: 2,
      title: "Wallet Balance Usage",
      icon: faWallet,
      color: "from-blue-500 to-blue-600",
      bgColor: "from-blue-50 to-blue-100",
      borderColor: "border-blue-200",
      items: [
        "The refunded amount credited to the IINSAF Wallet can be used for future transactions on our platform.",
        "Wallet balance cannot be withdrawn as cash or transferred to a bank account/UPI.",
        "Wallet balance is non-transferable between accounts."
      ]
    },
    {
      id: 3,
      title: "Refund Eligibility",
      icon: faCheckCircle,
      color: "from-green-500 to-green-600",
      bgColor: "from-green-50 to-green-100",
      borderColor: "border-green-200",
      items: [
        "A refund (in the form of wallet credit) may be applicable in the following cases:",
        "• Payment made for an advertisement that is later rejected or not approved by IINSAF.",
        "• Technical error or duplicate payment during a transaction.",
        "• Services not delivered due to a system issue on our side."
      ]
    },
    {
      id: 4,
      title: "Non-Refundable Cases",
      icon: faBan,
      color: "from-orange-500 to-orange-600",
      bgColor: "from-orange-50 to-orange-100",
      borderColor: "border-orange-200",
      items: [
        "Refunds (wallet credit) will not be applicable in the following cases:",
        "• Advertisements or content rejected due to violation of our Terms & Conditions or Content Policy.",
        "• Misuse of the platform, fraud, scams, or policy violations.",
        "• Payments made voluntarily by the user and later canceled by choice."
      ]
    },
    {
      id: 5,
      title: "Processing Time",
      icon: faClock,
      color: "from-purple-500 to-purple-600",
      bgColor: "from-purple-50 to-purple-100",
      borderColor: "border-purple-200",
      items: [
        "Eligible refunds (wallet credits) will be processed within 7–10 working days after verification.",
        "Users will be notified via email/SMS once the wallet credit has been successfully processed."
      ]
    },
    {
      id: 6,
      title: "Cancellation Policy",
      icon: faExclamationTriangle,
      color: "from-indigo-500 to-indigo-600",
      bgColor: "from-indigo-50 to-indigo-100",
      borderColor: "border-indigo-200",
      items: [
        "Once an advertisement or service is booked and approved, it cannot be canceled by the user for a cash refund.",
        "In special cases, if the service is canceled before approval, the amount may be refunded to the wallet balance only."
      ]
    },
    {
      id: 7,
      title: "Contact Us",
      icon: faPhone,
      color: "from-cyan-500 to-cyan-600",
      bgColor: "from-cyan-50 to-cyan-100",
      borderColor: "border-cyan-200",
      items: [
        "Phone: 9992396623",
        "Email: iinsafofficial@gmail.com",
        "Website: www.iinsaf.com"
      ]
    }
  ];

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-blue-50 pt-20 pb-8 px-2 sm:px-4">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.95 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-7xl mx-auto mb-12"
      >
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12 md:p-16 lg:p-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-8"
          >
            <div className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
              REFUND & CANCELLATION POLICY
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
                Refund Policy
              </span>{' '}
              – IINSAF
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-orange-600 mx-auto rounded-full mb-6"></div>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              At IINSAF, we value the trust of our users, including{' '}
              <span className="font-semibold text-blue-600">Reporters</span> and{' '}
              <span className="font-semibold text-green-600">Advertisers</span>. 
              This Refund & Cancellation Policy explains how payments and refunds are handled on our platform.
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Refund Policy Section */}
      <div className="w-full max-w-7xl mx-auto">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-6"
        >
          {refundSections.map((section, index) => (
            <motion.div
              key={section.id}
              variants={staggerItem}
              className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden hover:shadow-2xl transition-all duration-300"
            >
              <div className={`p-6 sm:p-8 bg-gradient-to-r ${section.bgColor} border-b ${section.borderColor}`}>
                <div className="flex items-center mb-6">
                  <div className={`w-12 h-12 bg-gradient-to-r ${section.color} rounded-xl flex items-center justify-center mr-4 shadow-lg`}>
                    <FontAwesomeIcon icon={section.icon} className="text-white text-xl" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                    {section.id}. {section.title}
                  </h2>
                </div>
                
                <div className="space-y-3">
                  {section.items.map((item, itemIndex) => (
                    <motion.div
                      key={itemIndex}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: itemIndex * 0.1 }}
                      className="flex items-start"
                    >
                      <FontAwesomeIcon 
                        icon={faCheckCircle} 
                        className={`text-${section.color.split('-')[1]}-500 mt-1 mr-3 flex-shrink-0`} 
                      />
                      <span className="text-gray-700 leading-relaxed">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl shadow-xl p-6 sm:p-8 text-white"
        >
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4 shadow-lg">
              <FontAwesomeIcon icon={faHeart} className="text-white text-xl" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold">Need Refund Support?</h2>
          </div>
          <p className="text-white/90 mb-6 text-lg">
            Have questions about refunds or need assistance with your wallet balance? Our support team is here to help.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faPhone} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Phone</p>
                <a href="tel:9992396623" className="text-white/90 hover:text-white transition-colors duration-300">
                  9992396623
                </a>
              </div>
            </div>
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faEnvelope} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Email</p>
                <a href="mailto:iinsafofficial@gmail.com" className="text-white/90 hover:text-white transition-colors duration-300">
                  iinsafofficial@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faGlobe} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Website</p>
                <a href="https://www.iinsaf.com" className="text-white/90 hover:text-white transition-colors duration-300" target="_blank" rel="noopener noreferrer">
                  www.iinsaf.com
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RefundPolicy;